import { useEffect, useRef } from "react";

// Live audio waveform visualiser driven by a MediaStream (Web Audio AnalyserNode).
// Renders animated bars while recording so the user sees their voice being captured.
export function RecordingWaveform({ stream, height = 26, color = "#dc2626", className = "" }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!stream || !canvasRef.current) return;
    let raf;
    let audioCtx;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 128;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const bars = 28;
      const step = Math.max(1, Math.floor(data.length / bars));
      const draw = () => {
        raf = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(data);
        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);
        const bw = w / bars;
        for (let i = 0; i < bars; i++) {
          const v = data[i * step] / 255;
          const bh = Math.max(2, v * h);
          ctx.fillStyle = color;
          const x = i * bw + 1;
          const y = (h - bh) / 2;
          const w2 = Math.max(1, bw - 2);
          if (ctx.roundRect) {
            ctx.beginPath();
            ctx.roundRect(x, y, w2, bh, Math.min(1.5, w2 / 2));
            ctx.fill();
          } else {
            ctx.fillRect(x, y, w2, bh);
          }
        }
      };
      draw();
    } catch { /* Web Audio unsupported — skip visualiser */ }
    return () => { cancelAnimationFrame(raf); try { audioCtx?.close(); } catch { /* ignore */ } };
  }, [stream, color]);
  return (
    <canvas ref={canvasRef} width={140} height={height} data-testid="recording-waveform"
      className={className} style={{ height, width: "100%" }} />
  );
}
